import { returnDV01ForSize, returnSizeForDV01 } from "@/lib/businessRules/dv01Rule";
import {
  BOND_SIZE_ROUNDING_STEP,
  returnRoundedSize,
} from "@/lib/businessRules/minimumSizeRule";
import { findBondSecurityByIsin } from "@/store/bondSecuritiesStoreACTIONS";
import { findFutureSecurityBySymbol } from "@/store/futureSecuritiesStoreACTIONS";
import {
  IBondTicket,
  IFutureTicket,
  TicketInstrument,
} from "@/store/tradeFromScratchStore";

// LQC-2941 (spec F3) - which tickets carry a DV01, and the size change a typed DV01 makes.
// Bond and Future only; the spec leaves the other instruments as an open question.

export const DV01_INSTRUMENTS: TicketInstrument[] = [
  TicketInstrument.Bond,
  TicketInstrument.Future,
];

export const NO_RISK_MESSAGE = "No risk for this security";

export function returnTicketRisk(
  instrument: TicketInstrument,
  ticket: unknown,
): number | null {
  if (!ticket) return null;
  if (instrument === TicketInstrument.Bond) {
    return findBondSecurityByIsin((ticket as IBondTicket).isin)?.risk ?? null;
  }
  if (instrument === TicketInstrument.Future) {
    return findFutureSecurityBySymbol((ticket as IFutureTicket).bloombergSymbol)?.risk ?? null;
  }
  return null;
}

// A future's size is its contract count, which is what it sends as `nominal`.
function returnTicketSize(instrument: TicketInstrument, ticket: unknown): number | null {
  if (instrument === TicketInstrument.Bond) return (ticket as IBondTicket).nominal;
  if (instrument === TicketInstrument.Future) return (ticket as IFutureTicket).contractCount;
  return null;
}

export function returnTicketDV01(instrument: TicketInstrument, ticket: unknown): number | null {
  if (!ticket || !DV01_INSTRUMENTS.includes(instrument)) return null;
  return returnDV01ForSize(
    returnTicketRisk(instrument, ticket),
    returnTicketSize(instrument, ticket),
  );
}

// Typing a DV01 sets the size: a bond to its 10k step, a future to whole contracts.
export function returnDV01SizeChanges(
  instrument: TicketInstrument,
  ticket: unknown,
  dv01: number | null,
): Partial<IBondTicket> | Partial<IFutureTicket> {
  const size = returnSizeForDV01(returnTicketRisk(instrument, ticket), dv01);
  if (size === null) return {};
  if (instrument === TicketInstrument.Bond) {
    return { nominal: returnRoundedSize(size, BOND_SIZE_ROUNDING_STEP) };
  }
  if (instrument === TicketInstrument.Future) {
    return { contractCount: Math.max(1, Math.round(size)) };
  }
  return {};
}
